// Business-hours types + open/closed evaluation, ported from the backend
// chatolam/src/services/business-hours.ts (isInboxOpen). Kept self-contained so
// the agent-ui bundle does not pull in server code. Supports the richer `weekly`
// map AND the legacy `schedule` array, holidays, and timezone via Intl.

export interface BusinessHours {
  timezone?: string;
  schedule?: Array<{ day: 0 | 1 | 2 | 3 | 4 | 5 | 6; openMin: number; closeMin: number }>;
  tz?: string;
  weekly?: Partial<
    Record<'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat', { open: string; close: string }>
  >;
  holidays?: string[];
}

const DAY_KEYS: Array<'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat'> = [
  'sun',
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
];

function parseHHMM(s: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

// Returns true if the inbox is currently within its configured business hours.
// Mirrors backend isInboxOpen(): supports `weekly` and legacy `schedule`,
// holidays, and timezone via Intl. If no businessHours configured, returns true.
export function isInboxOpen(
  hours: BusinessHours | undefined | null,
  nowMs: number = Date.now()
): boolean {
  if (!hours) return true;
  const tz = hours.tz || hours.timezone || 'UTC';
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date(nowMs));
  } catch {
    return true;
  }
  const wd = parts.find((p) => p.type === 'weekday')?.value;
  const year = parts.find((p) => p.type === 'year')?.value ?? '';
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  const day = parts.find((p) => p.type === 'day')?.value ?? '';
  const hour = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10);
  const minute = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10);
  const dayMap: Record<string, 0 | 1 | 2 | 3 | 4 | 5 | 6> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  const dayNum = wd ? dayMap[wd] : undefined;
  if (dayNum === undefined) return true;
  const todayIso = `${year}-${month}-${day}`;
  if (hours.holidays?.includes(todayIso)) return false;
  const minOfDay = hour * 60 + minute;

  // Prefer richer `weekly` map.
  if (hours.weekly) {
    const slot = hours.weekly[DAY_KEYS[dayNum]];
    if (!slot) return false;
    const openMin = parseHHMM(slot.open);
    const closeMin = parseHHMM(slot.close);
    if (openMin === null || closeMin === null) return true;
    return minOfDay >= openMin && minOfDay < closeMin;
  }

  // Fallback to legacy schedule array.
  for (const s of hours.schedule ?? []) {
    if (s.day === dayNum && minOfDay >= s.openMin && minOfDay < s.closeMin) return true;
  }
  return false;
}

// True when the inbox has any usable business-hours configuration. Used to
// decide whether to render an Open/Closed pill vs. "Open 24/7".
export function hasBusinessHours(hours: BusinessHours | undefined | null): boolean {
  if (!hours) return false;
  const hasWeekly = !!hours.weekly && Object.keys(hours.weekly).length > 0;
  const hasSchedule = Array.isArray(hours.schedule) && hours.schedule.length > 0;
  return hasWeekly || hasSchedule;
}
